import React, { ReactElement, useContext, useEffect, useRef } from "react";
import SurveyMarkdown from "../SurveyMarkdown/SurveyMarkdown";
import "../../SurveyVars.css";
import "./SurveyFormItem.css";
import { useSyncRefWidth } from "../../Util/useSyncWidth";
import { SurveyStepContext } from "../../Util/surveyStepContext";
import useDesignerSelectedFormItem, { designSurveyItem, onDesignerSelectedItemChange } from "../../Util/useDesignerSelectedFormItem";
import { SurveyFormStyle } from "../../../../types/Survey";

export interface SurveyFormItemProps {
    label?: string;
    labelFor?: string;
    children: React.ReactNode;
    validationError?: string | ReactElement;
    labelPosition?: "top" | "left";
    bottomBorder?: boolean;
    topBorder?: boolean;
    className?: string;
    variant?: SurveyFormStyle;
    index?: number;
    answerFormat?: string;
    useFieldsetFormat?: boolean;
}

export default function (props: SurveyFormItemProps) {
    let surveyStepContext = useContext(SurveyStepContext)!;
    let designerSelectedFormItem = useDesignerSelectedFormItem();
    const labelRef = useRef(null);
    useSyncRefWidth([
        [surveyStepContext.stepDefinition.identifier!, labelRef]
    ], [props.label]);

    let classes = ["survey-form-item"];
    let elementRef = useRef<HTMLDivElement>(null);

    classes.push(`survey-form-item-label-${props.labelPosition || "top"}`);

    if (props.bottomBorder) {
        classes.push("survey-form-item-bottom-border");
    }
    if (props.topBorder) {
        classes.push("survey-form-item-top-border");
    }
    if (props.variant == "cardBased") {
        classes.push("survey-form-item-card-based");
    }
    if (props.variant == "v2") {
        classes.push("survey-form-item-v2");
    }
    if (props.validationError) {
        classes.push("survey-form-item-invalid");
    }
    if (props.className) {
        classes.push(props.className);
    }

    if (surveyStepContext.mode === "Design") {
        classes.push("survey-form-item-design-mode");
    }
    if (surveyStepContext.mode === "Design" && (surveyStepContext.stepDefinition?.type == "FormStep" || surveyStepContext.stepDefinition?.type == "ConsentFormStep" || surveyStepContext.stepDefinition?.type == "InstructionStep")) {
        classes.push("survey-form-item-form-design");
    }
    if (designerSelectedFormItem !== undefined &&
        surveyStepContext.mode === "Design" &&
        designerSelectedFormItem.stepIndex === surveyStepContext.surveyDefinition.steps.indexOf(surveyStepContext.stepDefinition) &&
        designerSelectedFormItem.formItemIndex === props.index) {
        classes.push("survey-form-item-designing");
    }

    function selectFormItemForDesigner() {
        if (surveyStepContext.mode === "Design" && (surveyStepContext.stepDefinition?.type == "FormStep" || surveyStepContext.stepDefinition?.type == "InstructionStep" || surveyStepContext.stepDefinition?.type == "ConsentFormStep") && props.index != undefined) {
            designSurveyItem({ stepIndex: surveyStepContext.surveyDefinition.steps.indexOf(surveyStepContext.stepDefinition), formItemIndex: designerSelectedFormItem.formItemIndex == props.index ? undefined : props.index });
        }
    }

    // Since we use buttons as placeholder for date inputs
    // Clicking the label needs to click the button
    function clickButton(id?: string) {
        if (id) {
            let button = document.getElementById(id);
            if (button && button.tagName === "BUTTON") {
                button.click();
            }
        }
    }

    useEffect(() => {
        return onDesignerSelectedItemChange((newFormItem, oldFormItem) => {
            //if we're changing steps, it was clicked so it is already in view
            if (newFormItem?.stepIndex !== oldFormItem?.stepIndex) return;
            if (!elementRef.current) return;
            if (newFormItem?.stepIndex === surveyStepContext.surveyDefinition.steps.indexOf(surveyStepContext.stepDefinition) && newFormItem.formItemIndex === props.index) {
                elementRef.current?.scrollIntoView({ block: "nearest" });
            }
        });
    }, []);

    return <div className={classes.join(" ")} onClick={(event) => { event.stopPropagation(); selectFormItemForDesigner(); }} ref={elementRef}>
        {props.useFieldsetFormat ? (
            <fieldset className="survey-form-item-inner">
                <legend>
                    {props.label &&
                        <div ref={props.labelPosition == "left" ? labelRef : undefined} className="survey-form-item-header" onClick={() => clickButton(props.labelFor)}>
                            <SurveyMarkdown inline text={props.label} />
                        </div>
                    }
                </legend>
                <div className="survey-form-item-children">
                    {props.children}
                </div>
            </fieldset>
        ) : (
            <div className="survey-form-item-inner">
                {props.label &&
                    <label ref={props.labelPosition == "left" ? labelRef : undefined} htmlFor={props.labelFor} className="survey-form-item-header" onClick={() => clickButton(props.labelFor)}>
                        <SurveyMarkdown inline text={props.label} />
                    </label>
                }
                <div className="survey-form-item-children">
                    {props.children}
                </div>
            </div>
        )}
        {props.validationError &&
            <div id={props.labelFor + "-error"} role="alert" className="survey-form-item-validation-error">{props.validationError}</div>
        }
        {props.index !== undefined &&
            <div className="survey-form-item-border"></div>
        }
    </div>
}